import React, { useEffect, useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useMutation } from '@tanstack/react-query'
import { FiWifi } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { useDebouncedCallback } from 'use-debounce'
import { useGeolocation } from '../../hooks/useGeolocation'

import history from '../../services/history'
import { Textbox, Button } from '../../components/Form'
import ProgressBar from '../../components/ProgressBar'
import scrollUtils from '../../util/scroll'

import {
    Container, Content
} from './styles'

import regex from '../../util/regex'
import masks from '../../util/masks'

import { api } from '../../services/api'
import { passwordChecker } from '../../services/password-checker'
import PasswordTextbox from '../../components/Form/PasswordTextbox'
import { validarCPF } from '../../util/validadores'

const validation = Yup.object({
    name: Yup.string()
        .required('Nome obrigatório.'),
    email: Yup.string()
        .email('E-mail inválido.')
        .required('E-mail obrigatório.'),
    phone: Yup.string()
        .matches(regex.mobile, 'Celular inválido.')
        .required('O WhatsApp é obrigatório.'),
    cpf: Yup.string()
        .test('validar-cpf', 'CPF inválido.', value => validarCPF(value || ''))
        .required('O CPF é obrigatório.'),
    password: Yup.string()
        .required('Senha obrigatória.')
        .min(6, 'A senha deve conter pelo menos 6 caracteres.'),
    'password-confirm': Yup.string()
        .required('Confirme a senha digitada.')
        .oneOf([Yup.ref('password')], 'As senhas não coincidem.'),
})

const initialValues = {
    name: '',
    email: '',
    phone: '',
    cpf: '',
    password: '',
    'password-confirm': ''
}

export default function SignUp() {
    const { registerPageAccess } = useGeolocation()

    const cartItemString = localStorage.getItem('sigas:carrinho')

    const plano_contratacao = cartItemString ? JSON.parse(cartItemString) : null

    const [userData, setUserData] = useState(null)
    const [forcaSenha, setForcaSenha] = useState(0)

    const { mutate: handleSubmit, isPending } = useMutation({
        mutationKey: ['confirm-user'],
        mutationFn: async data => {
            await api.post('usuario', data)

            setUserData(data)

            localStorage.setItem('sigas-confirmation-account-temp-data', data.email)

            history.push({
                pathname: '/confirmacao_conta',
                state: {
                    usuario_criado: true
                }
            })
        },
        onError: e => {
            toast.error(e.msg)
        }
    })

    async function handleCheckPassword(password) {
        const { data } = await passwordChecker.post('/', { password })

        setForcaSenha(data.score)
    }

    useEffect(() => {
        if (plano_contratacao) {
            registerPageAccess({
                plano: plano_contratacao.id
            })
        } else {
            registerPageAccess()
        }
    }, [])

    useEffect(() => {
        scrollUtils.toTop()
    }, [])

    const handleCheckPasswordDebounced = useDebouncedCallback(handleCheckPassword, 1000)

    return (
        <Container>
            <div className="flex flex-col">
                <Content className="animated fadeIn faster delay-300ms flex flex-col items-center bg-white text-slate-800">
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validation}
                        onSubmit={handleSubmit}
                    >
                        {({ values }) => (
                            <Form>
                                <h1 className="text-slate-800">Crie sua conta</h1>

                                <Textbox name="name" label="Seu nome" />

                                <Textbox name="email" label="Seu e-mail" />

                                <Textbox name="phone" label="Seu WhatsApp" mask={masks.mobile} />

                                <Textbox name="cpf" label="Seu CPF" mask={masks.cpf} />

                                <PasswordTextbox
                                    name="password"
                                    label="Senha"
                                    onChange={pwd => {
                                        if (pwd) {
                                            handleCheckPasswordDebounced(pwd)
                                        } else {
                                            setForcaSenha(0)
                                        }
                                    }}
                                />

                                <ProgressBar
                                    visible={forcaSenha > 0 && !!values.password.length}
                                    value={forcaSenha}
                                    rotulada
                                    rotulos={[
                                        { label: 'Sem senha', color: 'bg-gray-400' },
                                        { label: 'Muito fraca', color: 'bg-red-500' },
                                        { label: 'Fraca', color: 'bg-orange-500' },
                                        { label: 'Média', color: 'bg-yellow-500' },
                                        { label: 'Forte', color: 'bg-lime-500' },
                                        { label: 'Muito forte', color: 'bg-emerald-500' }
                                    ]}
                                />

                                <PasswordTextbox
                                    name="password-confirm"
                                    label="Repetir senha"
                                />

                                <Button type="submit" loading={isPending}>Criar conta</Button>

                                <div
                                    className="text-sky-700 mt-4 cursor-pointer hover:opacity-70 hover:underline"
                                    onClick={() => {
                                        history.push('/login')
                                    }}
                                >
                                    Já tenho login
                                </div>
                            </Form>
                        )}
                    </Formik>

                    {userData && (
                        <div className="flex flex-col gap-4 bg-yellow-500 p-2 rounded-md mt-6 text-slate-800 animated flash">
                            <p style={{ fontWeight: 800 }}>Sua conta foi criada!</p>

                            <p>{`Acesse o e-mail ${userData.email} e clique no link de confirmação.`}</p>
                        </div>
                    )}
                </Content>

                <p className="mx-4 text-slate-300 flex gap-2 text-xs w-72 mb-12 animated fadeIn delay-1s">
                    <FiWifi size={23} />
                    <span>Este aplicativo precisa de conexão com a internet para funcionar.</span>
                </p>
            </div>
        </Container>
    )
}
